import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import Sidebar from './Sidebar';
import Header from './Header';
import DemoBanner from './DemoBanner';
import logger from '../utils/logger';

// Brand captured once from index.html so it stays correct across rebrands.
const BRAND = (typeof document !== 'undefined' && document.title) || 'Fleet';

const PAGE_TITLES = {
  '/dashboard': 'Контролна табла',
  '/vehicles': 'Возила',
  '/devices': 'GPS Уреди',
  '/map': 'Мапа',
  '/locations': 'Локации',
  '/alarms': 'Аларми',
  '/routes': 'Рути',
  '/route-export': 'Извозена рута',
  '/speed-dashboard': 'Брзина & Растојание',
  '/geofences': 'Гео-ограничувања',
  '/maintenance': 'Сервис',
  '/fuel-management': 'Гориво',
  '/drivers': 'Возачи',
  '/analytics': 'Аналитики',
  '/users': 'Корисници',
  '/tenants': 'Клиенти',
  '/demo-accounts': 'Демо сметки',
  '/reports': 'Извештаи',
  '/profile': 'Профил',
  '/logs': 'Системски логови',
  '/alerts-dashboard': 'Системски аларми',
  '/installer-assistant': 'Технички Асистент',
  '/settings/map': 'Поставки за мапа',
  '/inventory': 'Залихи',
};

const Layout = () => {
  // На десктоп (>768px): sidebar видлив по default (collapsed = false)
  // На мобилен (<=768px): sidebar скриен по default (collapsed = true)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return window.innerWidth <= 768;
  });
  const [isMobile, setIsMobile] = useState(() => {
    return window.innerWidth <= 768;
  });
  const { user } = useAuth();
  const { connected } = useSocket();
  const navigate = useNavigate();
  const location = useLocation();

  // Keep the browser tab title in sync with the current page
  useEffect(() => {
    const path = location.pathname;
    const key = Object.keys(PAGE_TITLES).find(p => path === p || path.startsWith(p + '/'));
    document.title = key ? `${PAGE_TITLES[key]} · ${BRAND}` : BRAND;
  }, [location.pathname]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      const narrow = window.innerWidth <= 480;
      const wasMobile = isMobile;
      
      setIsMobile(mobile);
      
      logger.log('Screen size check:', {
        width: window.innerWidth,
        mobile,
        narrow,
        wasMobile,
        currentCollapsed: sidebarCollapsed
      });
      
      // Ако се префрла од десктоп на мобилен, затвори го sidebar
      if ((mobile || narrow) && !wasMobile) {
        setSidebarCollapsed(true);
      }
      // Ако се префрла од мобилен на десктоп, отвори го sidebar
      else if (!mobile && wasMobile) {
        setSidebarCollapsed(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [isMobile, sidebarCollapsed]);

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    logger.log('Toggle sidebar clicked:', { 
      currentState: sidebarCollapsed, 
      newState,
      isMobile,
      willBeOpen: newState === false
    });
    setSidebarCollapsed(newState);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setSidebarCollapsed(true);
    }
  };

  // На мобилен или тесен екран, sidebar е отворен само кога не е collapsed
  const isSidebarOpen = !sidebarCollapsed && isMobile;

  logger.log('Layout render:', { 
    sidebarCollapsed, 
    isMobile, 
    isSidebarOpen,
    windowWidth: window.innerWidth,
    mainLayoutClass: isSidebarOpen ? 'main-layout sidebar-open' : 'main-layout'
  });

  return (
    <div className={`main-layout ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {/* Кликабилен overlay за мобилен - затвори sidebar кога кликнеш на overlay-от */}
      {isSidebarOpen && (
        <div 
          className="mobile-overlay"
          onClick={closeSidebar}
          onTouchStart={closeSidebar}
          aria-label="Close sidebar"
        />
      )}
      
      <Sidebar 
        collapsed={sidebarCollapsed} 
        currentPath={location.pathname}
        onNavigate={navigate}
        isMobile={isMobile}
        onCloseSidebar={closeSidebar}
        open={isSidebarOpen}
      />
      
      <div className={`main-content ${sidebarCollapsed ? 'expanded' : ''}`}>
        <Header 
          user={user}
          socketConnected={connected}
          onToggleSidebar={toggleSidebar}
          sidebarCollapsed={sidebarCollapsed}
          isMobile={isMobile}
        />
        
        <DemoBanner />

        <div className="content-body">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default Layout;





























